import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLanguage } from '@/contexts/LanguageContext';
import { Eye, EyeOff, Mail, Lock, Shield, AlertCircle } from 'lucide-react';
import { logSecurityEvent } from '@/lib/security';

const AdminLogin = () => {
  const { signIn, user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isSpanish = language === 'es';

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Security tracking
  useEffect(() => {
    // Log admin login page access
    logSecurityEvent('admin_login_page_accessed', {
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      ip: 'client-side' // In production, get from server
    }, 'medium');
  }, []);

  // Redirect logic for authenticated users
  useEffect(() => {
    if (user && !authLoading) {
      if (isAdmin) {
        navigate('/admin');
      } else {
        // Non-admin users trying to access admin login
        logSecurityEvent('non_admin_attempted_admin_login', {
          userId: user.id,
          timestamp: Date.now()
        }, 'high');
        setError(isSpanish ? 'Acceso denegado: Se requieren privilegios de administrador' : 'Access denied: Admin privileges required');
      }
    }
  }, [user, isAdmin, authLoading, navigate, isSpanish]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Log login attempt
    logSecurityEvent('admin_login_attempt', {
      email: loginData.email,
      timestamp: Date.now()
    }, 'medium');

    try {
      const { error } = await signIn(loginData.email, loginData.password);

      if (error) {
        logSecurityEvent('admin_login_failed', {
          email: loginData.email,
          error: error.message,
          timestamp: Date.now()
        }, 'high');

        if (error.message.includes('Invalid login credentials')) {
          setError(isSpanish ? 'Credenciales de administrador inválidas' : 'Invalid admin credentials');
        } else {
          setError(error.message);
        }
      } else {
        // Success will be handled by useEffect when user state updates
        logSecurityEvent('admin_login_success', {
          email: loginData.email,
          timestamp: Date.now()
        }, 'low');
      }
    } catch (err) {
      logSecurityEvent('admin_login_error', {
        email: loginData.email,
        error: String(err),
        timestamp: Date.now()
      }, 'high');
      setError(isSpanish ? 'Error en el sistema de autenticación' : 'Authentication system error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900/20 via-background to-orange-900/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Admin Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-12 w-12 text-red-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-red-600">CruzaSmart</h1>
              <p className="text-sm font-semibold text-red-500 uppercase tracking-wider">
                {isSpanish ? 'Panel Administrativo' : 'Admin Panel'}
              </p>
            </div>
          </div>
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-700 dark:text-red-300">
              {isSpanish 
                ? 'Acceso restringido para administradores autorizados'
                : 'Restricted access for authorized administrators only'
              }
            </p>
          </div>
        </div>

        <Card className="border-red-200 dark:border-red-800">
          <CardHeader className="bg-red-50 dark:bg-red-950/30">
            <CardTitle className="text-center text-red-700 dark:text-red-300">
              {isSpanish ? 'Acceso de Administrador' : 'Administrator Access'}
            </CardTitle>
          </CardHeader>
          <CardContent className="mt-6">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">
                  {isSpanish ? 'Correo de Administrador' : 'Admin Email'}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder={isSpanish ? 'admin@cruzasmart.com' : 'admin@cruzasmart.com'}
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="pl-10 border-red-200 focus:border-red-400"
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-password">
                  {isSpanish ? 'Contraseña de Administrador' : 'Admin Password'}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={isSpanish ? 'Contraseña segura' : 'Secure password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="pl-10 pr-10 border-red-200 focus:border-red-400"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700 text-white" 
                disabled={loading}
              >
                {loading 
                  ? (isSpanish ? 'Verificando...' : 'Verifying...') 
                  : (isSpanish ? 'Acceder al Panel' : 'Access Panel')
                }
              </Button>
            </form>

            {/* Security Notice */}
            <div className="mt-6 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded">
              <div className="flex items-start space-x-2">
                <Shield className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-xs text-amber-700 dark:text-amber-300">
                  <p className="font-semibold mb-1">
                    {isSpanish ? 'Aviso de Seguridad:' : 'Security Notice:'}
                  </p>
                  <p>
                    {isSpanish 
                      ? 'Este acceso está monitoreado. Todos los intentos se registran por seguridad.'
                      : 'This access is monitored. All attempts are logged for security purposes.'
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-sm text-muted-foreground"
              >
                {isSpanish ? '← Volver al sitio principal' : '← Back to main site'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-muted-foreground">
          <p>
            {isSpanish 
              ? 'Sistema administrativo seguro - Unauthorized access prohibited'
              : 'Secure administrative system - Unauthorized access prohibited'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;